import * as React from 'react';
import { useQuery } from 'react-query';
import type { Supplier } from 'contracts/Supplier';
import { api, applyQueryString } from 'services/api';
import { Formatter } from 'utils';

import { multiSelectorOptionsMapper, optionsMapper } from '../utils';

type LabelType = 'trade-name-and-document' | 'trade-name'
type HookOptions = {
  labelType?: LabelType,
}

const createLabel = (supplier: Supplier, labelType?: LabelType) => {
  const tradeName = supplier.tradeName;
  const document = Formatter.document(supplier.document, supplier.documentType);
  const numericDocumentLength = document.replace(/\D/g, '').length;

  switch (labelType) {
    case 'trade-name':
      return tradeName;
    case 'trade-name-and-document':
    default:
      return numericDocumentLength >= 8
        ? [tradeName, document].join(' - ')
        : tradeName;
  }
};

export const useSupplierOptions = (options?: HookOptions) => {
  const fetchOptions = React.useCallback(async () => {
    const url = applyQueryString('/suppliers', {
      asList: true,
    });

    const { data } = await api.get<Supplier[]>(url);
    return data.map(supplier => ({
      value: supplier.id,
      label: createLabel(supplier, options?.labelType),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [options?.labelType]);

  const optionsQuery = useQuery({
    queryKey: ['fetch-supplier-options'],
    queryFn: fetchOptions,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  return {
    singleSelectOptions: optionsQuery.data?.map(optionsMapper) || [],
    multiSelectOptions: optionsQuery.data?.map(multiSelectorOptionsMapper) || [],
    loading: optionsQuery.isLoading,
  };
};